import axios from "axios";

export type AuthClientError = {
  message: string;
  code?: string;
  status?: number;
  details?: Record<string, string[]>;
};

type AuthErrorBody = {
  message?: string;
  error?: {
    message?: string;
    code?: string;
    details?: Record<string, string[]>;
  };
};

/** First field-level message from Adatis `error.details` (422 VALIDATION_ERROR). */
export const firstValidationDetailMessage = (
  details?: Record<string, string[]>
): string | undefined => {
  if (!details) {
    return undefined;
  }

  for (const messages of Object.values(details)) {
    if (Array.isArray(messages)) {
      const first = messages.find(
        (item) => typeof item === "string" && item.trim().length > 0
      );

      if (first) {
        return first;
      }
    }
  }

  return undefined;
};

export const resolveAuthClientError = (
  error: unknown,
  fallback: string
): AuthClientError => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as AuthErrorBody | undefined;
    const details = data?.error?.details;
    const fieldMessage = firstValidationDetailMessage(details);

    return {
      message:
        fieldMessage || data?.error?.message || data?.message || fallback,
      code: data?.error?.code,
      status: error.response?.status,
      details,
    };
  }

  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    typeof (error as { data?: unknown }).data === "string"
  ) {
    return { message: (error as { data: string }).data || fallback };
  }

  if (error instanceof Error && error.message) {
    return { message: error.message };
  }

  return { message: fallback };
};

export const applyAuthValidationDetails = (
  details: Record<string, string[]> | undefined,
  setFieldError: (field: string, message: string | undefined) => void,
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => void,
  allowedFields: string[]
): void => {
  if (!details) {
    return;
  }

  const allowed = new Set(allowedFields);

  for (const [field, messages] of Object.entries(details)) {
    if (!allowed.has(field)) {
      continue;
    }

    const message = messages?.find(
      (item) => typeof item === "string" && item.trim().length > 0
    );

    if (!message) {
      continue;
    }

    setFieldError(field, message);
    setFieldTouched(field, true, false);
  }
};

export const isAuthRateLimited = (error: AuthClientError): boolean =>
  error.status === 429 || error.code === "RATE_LIMIT_EXCEEDED";

export const digitsOnly = (value: string, maxLength = 6): string =>
  value.replace(/\D/g, "").slice(0, maxLength);

export const formatCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};
